(() => {
  if (customElements.get('stellar-engraving-preview')) return;
  const fonts = new Map();
  customElements.define('stellar-engraving-preview', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.detailFrame);
      this.abort = new AbortController();
      this.form = document.getElementById(this.dataset.formId);
      this.field = this.form?.querySelector('[name="properties[Personalisation]"]');
      this.choice = this.form?.querySelector('.product-personalisation-wrap input[type="checkbox"]');
      this.modal = this.closest('modal-dialog');
      this.artwork = this.querySelector('[data-engraving-artwork]');
      this.note = this.querySelector('[data-engraving-note]');
      this.selection = this.querySelector('[data-engraving-selection]');
      this.currentVariant = null;
      this.input = this.querySelector('[data-engraving-input]');
      this.composing = false;
      this.apply = this.querySelector('[data-engraving-apply]');
      this.orderStatus = this.querySelector('[data-engraving-order-status]');
      this.status = this.querySelector('[data-engraving-status]');
      this.counter = this.querySelector('[data-engraving-counter]');
      this.back = this.querySelector('[data-engraving-back]');
      this.toolbar = this.querySelector('[data-engraving-toolbar]');
      this.viewport = this.querySelector('[data-engraving-viewport]');
      this.reset = this.querySelector('[data-engraving-reset]');
      if (!this.field || !this.choice || !this.modal || !this.artwork || !this.input) return;
      const options = { signal: this.abort.signal };
      this.artwork.addEventListener('click', event => {
        if (event.target.closest('[data-engraving-zoom]')) this.setExpanded(!this.expanded);
      }, options);
      this.back?.addEventListener('click', () => this.setExpanded(false), options);
      this.reset?.addEventListener('click', () => {
        this.resetDetail();
        this.viewport?.focus({ preventScroll: true });
      }, options);
      this.viewport?.addEventListener('keydown', event => {
        if (!this.expanded || event.target !== this.viewport) return;
        const distance = event.shiftKey ? 240 : 80;
        const moves = { ArrowLeft: [-distance, 0], ArrowRight: [distance, 0], ArrowUp: [0, -distance], ArrowDown: [0, distance], PageUp: [0, -this.viewport.clientHeight * .8], PageDown: [0, this.viewport.clientHeight * .8] };
        if (event.key === 'Home') {
          event.preventDefault();
          this.resetDetail();
        } else if (moves[event.key]) {
          event.preventDefault();
          this.detailMoved = true;
          this.viewport.scrollLeft += moves[event.key][0];
          this.viewport.scrollTop += moves[event.key][1];
        }
      }, options);
      this.viewport?.addEventListener('pointerdown', event => {
        if (!this.expanded) return;
        this.detailMoved = true;
        if (event.pointerType === 'touch' || event.button !== 0) return;
        event.preventDefault();
        this.viewport.focus({ preventScroll: true });
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft, top: this.viewport.scrollTop };
        this.viewport.setPointerCapture(event.pointerId);
        this.viewport.setAttribute('data-dragging', '');
      }, options);
      this.viewport?.addEventListener('pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        this.viewport.scrollLeft = this.drag.left + this.drag.x - event.clientX;
        this.viewport.scrollTop = this.drag.top + this.drag.y - event.clientY;
      }, options);
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => this.viewport?.addEventListener(type, () => this.endDetailDrag(), options));
      this.viewport?.addEventListener('wheel', () => { if (this.expanded) this.detailMoved = true; }, { ...options, passive: true });
      this.modal.addEventListener('keydown', event => {
        if (!this.expanded) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.ignoreEscapeKeyup = true;
          this.setExpanded(false);
        } else if (event.key === 'Tab') {
          const focusable = Array.from(this.modal.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]'))
            .filter(element => element.tabIndex >= 0 && element.getClientRects().length);
          if (!focusable.length) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          const current = focusable.indexOf(document.activeElement);
          const next = (current + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length;
          focusable[next].focus();
        }
      }, { ...options, capture: true });
      this.modal.addEventListener('keyup', event => {
        if (event.key !== 'Escape' || (!this.expanded && !this.ignoreEscapeKeyup)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.ignoreEscapeKeyup = false;
        if (this.expanded) this.setExpanded(false);
      }, { ...options, capture: true });
      this.update = () => {
        const artworkAvailable = this.updateArtwork();
        const selected = this.choice.checked && !this.choice.disabled && !this.field.disabled;
        const limit = window.StellarEngraving?.maxLength || 22;
        this.field.maxLength = limit;
        this.input.maxLength = limit;
        this.input.disabled = false;
        if (!this.composing && this.input.value !== this.field.value) this.input.value = this.field.value;
        const draft = this.composing ? this.input.value : this.field.value;
        if (this.apply) {
          this.apply.disabled = this.choice.disabled;
          this.apply.checked = this.choice.checked && !this.choice.disabled;
        }
        if (this.orderStatus) this.orderStatus.textContent = selected ? this.dataset.selected : this.dataset.unselected;
        this.counter.textContent = `${draft.length} / ${limit}`;
        if (this.text) { this.text.textContent = ''; this.text.style.visibility = 'hidden'; }
        const engravingError = window.StellarEngraving?.error(draft, window.StellarEngraving.fontFor(this.form)) || '';
        this.input.setCustomValidity(engravingError);
        if (engravingError) this.input.setAttribute('aria-invalid', 'true');
        else this.input.removeAttribute('aria-invalid');
        if (engravingError) { this.status.textContent = engravingError; return; }
        if (!artworkAvailable) { this.status.textContent = this.dataset.unavailable; return; }
        if (!this.fontReady) {
          this.status.textContent = this.failed ? this.dataset.unavailable : this.dataset.loading;
          if (this.modal.hasAttribute('open') && !this.fontLoading && !this.failed) this.loadFont();
          return;
        }
        const value = draft.trim();
        this.text.style.fontFamily = this.fontFamily;
        this.text.style.fontWeight = this.fontConfiguration.weight;
        this.text.style.fontStyle = this.fontConfiguration.style;
        this.text.setAttribute('font-size', this.dataset.fontSize);
        this.text.textContent = value;
        if (this.modal.hasAttribute('open') && value) {
          let width;
          try { width = this.text.getBBox().width; }
          catch (_) { this.status.textContent = this.dataset.unavailable; return; }
          if (!Number.isFinite(width) || width <= 0) { this.status.textContent = this.dataset.unavailable; return; }
          // Keep the configured preview size and show a warning rather than clipped text.
          if (width > Number(this.dataset.maxWidth)) {
            this.status.textContent = this.dataset.overflow;
            return;
          }
        }
        this.text.style.visibility = 'visible';
        this.status.textContent = value ? '' : this.dataset.empty;
      };
      const syncText = event => {
        if (event.isComposing) this.composing = true;
        const limit = window.StellarEngraving?.maxLength || 22;
        this.field.value = this.input.value.slice(0, limit);
        this.field.dispatchEvent(new Event('input', { bubbles: true }));
      };
      this.input.addEventListener('compositionstart', () => { this.composing = true; }, options);
      this.input.addEventListener('input', syncText, options);
      this.input.addEventListener('compositionend', event => {
        this.composing = false;
        syncText(event);
      }, options);
      this.input.addEventListener('change', () => {
        this.field.dispatchEvent(new Event('change', { bubbles: true }));
      }, options);
      this.apply?.addEventListener('change', () => {
        if (this.choice.disabled) { this.update(); return; }
        this.choice.checked = this.apply.checked;
        this.choice.dispatchEvent(new Event('change', { bubbles: true }));
        this.update();
      }, options);
      this.input.addEventListener('keydown', event => {
        if (event.key === 'Enter' && !event.isComposing && !this.composing) event.preventDefault();
      }, options);
      ['input', 'change', 'stellar:purchase-state'].forEach(type => this.form.addEventListener(type, this.update, options));
      this.form.addEventListener('reset', () => queueMicrotask(this.update), options);
      document.addEventListener('click', event => {
        const opener = event.target.closest('modal-opener');
        if (!opener) return;
        const target = opener.getAttribute('data-modal');
        if (target === `#${this.modal.id}`) {
          document.querySelectorAll('modal-dialog[open], quick-add-modal[open]').forEach(modal => {
            const owningQuickAdd = this.form?.closest('quick-add-modal');
            if (modal !== this.modal && modal !== owningQuickAdd && typeof modal.hide === 'function') modal.hide();
          });
          document.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
        } else if (this.modal.hasAttribute('open')) this.modal.hide();
      }, { ...options, capture: true });
      const updateViewport = () => {
        const viewport = window.visualViewport;
        this.modal.toggleAttribute('data-engraving-compact', (viewport?.height || window.innerHeight) <= 520);
        this.modal.style.setProperty('--engraving-viewport-height', (viewport?.height || window.innerHeight) + 'px');
        this.modal.style.setProperty('--engraving-viewport-top', (viewport?.offsetTop || 0) + 'px');
        if (this.expanded && !this.detailMoved) this.resetDetail();
      };
      window.visualViewport?.addEventListener('resize', updateViewport, options);
      window.visualViewport?.addEventListener('scroll', updateViewport, options);
      window.addEventListener('resize', updateViewport, options);
      this.observer = new MutationObserver(() => {
        if (this.modal.hasAttribute('open')) {
          updateViewport();
          this.modal.scrollTop = 0;
          const content = this.modal.querySelector('.product-popup-modal__content');
          if (content) content.scrollTop = 0;
        } else this.setExpanded(false, false);
        this.update();
      });
      updateViewport();
      this.observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      this.update();
    }
    setExpanded(expanded, restoreFocus = true) {
      const zoom = this.artwork?.querySelector('[data-engraving-zoom]');
      if (expanded && (!zoom || !this.modal.hasAttribute('open'))) return;
      if (Boolean(this.expanded) === expanded) return;
      const content = this.modal.querySelector('.product-popup-modal__content');
      if (expanded) {
        this.editorScrollTop = content?.scrollTop || 0;
        this.editorSelection = [this.input.selectionStart, this.input.selectionEnd, this.input.selectionDirection];
        this.detailWidth = Math.min(6000, Math.max(1000, 40000 / (Number(this.dataset.fontSize) || 14)));
        this.viewport?.style.setProperty('--engraving-detail-width', this.detailWidth + 'px');
      }
      cancelAnimationFrame(this.detailFrame);
      this.endDetailDrag();
      this.expanded = expanded;
      this.modal.toggleAttribute('data-engraving-expanded', expanded);
      if (this.toolbar) this.toolbar.hidden = !expanded;
      if (zoom) {
        zoom.setAttribute('aria-expanded', String(expanded));
        zoom.tabIndex = expanded ? -1 : 0;
        zoom.disabled = expanded;
        if (expanded) zoom.setAttribute('aria-hidden', 'true');
        else zoom.removeAttribute('aria-hidden');
        const image = zoom.querySelector('img');
        if (image) {
          if (!image.dataset.editorSizes) image.dataset.editorSizes = image.sizes || '(min-width: 750px) 280px, 280px';
          image.sizes = expanded ? this.detailWidth + 'px' : image.dataset.editorSizes;
        }
      }
      if (content) content.scrollTop = expanded ? 0 : this.editorScrollTop || 0;
      if (this.viewport) {
        this.viewport.tabIndex = expanded ? 0 : -1;
        if (expanded) this.viewport.setAttribute('role', 'region');
        else this.viewport.removeAttribute('role');
      }
      if (expanded) {
        this.resetDetail();
        this.detailFrame = requestAnimationFrame(() => {
          if (!this.expanded) return;
          this.resetDetail();
          this.viewport?.focus({ preventScroll: true });
        });
      } else {
        if (this.viewport) { this.viewport.scrollLeft = 0; this.viewport.scrollTop = 0; }
        if (this.editorSelection) this.input.setSelectionRange(...this.editorSelection);
        if (restoreFocus && this.modal.hasAttribute('open')) zoom?.focus({ preventScroll: true });
      }
    }
    resetDetail() {
      if (!this.expanded || !this.viewport || !this.text) return;
      const svg = this.artwork.querySelector('svg');
      const width = Number(svg?.getAttribute('viewBox')?.split(/\s+/)[2]) || 1000;
      const scale = this.detailWidth / width;
      const x = Number(this.text.getAttribute('x')) * scale;
      const y = Number(this.text.getAttribute('y')) * scale;
      const anchor = this.text.getAttribute('text-anchor');
      const start = anchor === 'start' ? .2 : anchor === 'end' ? .8 : .5;
      this.viewport.scrollLeft = Math.max(0, x - this.viewport.clientWidth * start);
      this.viewport.scrollTop = Math.max(0, y - this.viewport.clientHeight / 2);
      this.detailMoved = false;
    }
    endDetailDrag() {
      const id = this.drag?.id;
      this.drag = null;
      this.viewport?.removeAttribute('data-dragging');
      if (id !== undefined && this.viewport?.hasPointerCapture(id)) this.viewport.releasePointerCapture(id);
    }
    updateArtwork() {
      if (this.hasAttribute('data-variant-preview')) {
        const id = this.form.querySelector('[name="id"]')?.value || '';
        if (id !== this.currentVariant) {
          if (this.expanded) this.setExpanded(false);
          this.currentVariant = id;
          const template = Array.from(this.querySelectorAll('template[data-engraving-variant]'))
            .find(item => item.dataset.engravingVariant === id);
          this.artwork.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
          if (this.selection) this.selection.textContent = template?.dataset.label || '';
        }
      }
      const image = this.artwork.querySelector('[data-engraving-width]');
      this.text = this.artwork.querySelector('[data-engraving-text]');
      this.dataset.engravingWidth = image?.dataset.engravingWidth || '';
      this.dataset.maxWidth = image?.dataset.maxWidth || '';
      this.dataset.fontSize = image?.dataset.fontSize || this.dataset.fontSize;
      const font = {
        source: image?.dataset.fontSource || 'asset',
        url: image?.dataset.fontUrl || (image?.dataset.fontSource === 'system' ? '' : this.dataset.fontUrl),
        family: image?.dataset.fontFamily || 'StellarAutography',
        weight: image?.dataset.fontWeight || '400',
        style: image?.dataset.fontStyle || 'normal'
      };
      const key = JSON.stringify(font);
      if (key !== this.fontKey) {
        this.fontKey = key;
        this.fontConfiguration = font;
        this.fontReady = false;
        this.fontLoading = false;
        this.failed = false;
      }
      if (this.note) this.note.textContent = image?.dataset.disclaimer || this.dataset.unavailable;
      return Boolean(image && this.text);
    }
    async loadFont() {
      this.fontLoading = true;
      const signal = this.abort.signal;
      const key = this.fontKey;
      const config = this.fontConfiguration;
      try {
        let family = config.family;
        if (config.source !== 'system') {
          const url = new URL(config.url, location.href);
          if (url.protocol !== 'https:') throw new Error('Invalid font URL');
          const cacheKey = JSON.stringify([url.href, config.weight, config.style]);
          if (!fonts.has(cacheKey)) {
            const name = 'StellarEngraving' + fonts.size;
            const font = new FontFace(name, 'url(' + JSON.stringify(url.href) + ')', { weight: config.weight, style: config.style });
            fonts.set(cacheKey, {family:name, loaded:font.load().then(loaded => { document.fonts.add(loaded); return loaded; })});
          }
          const cached = fonts.get(cacheKey);
          await cached.loaded;
          family = cached.family;
        }
        if (signal.aborted || key !== this.fontKey) return;
        this.fontFamily = family;
        this.fontReady = true;
      } catch (_) { if (!signal.aborted && key === this.fontKey) this.failed = true; }
      finally { if (!signal.aborted && key === this.fontKey) { this.fontLoading = false; if (this.isConnected) this.update(); } }
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.detailFrame); this.endDetailDrag(); }
  });
})();
